import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenancy } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";
import { useToast } from "@/components/shared/Toast";
import type { BulkTenancyResult, DeviceRow } from "@/types/api";

interface Props {
  devices: DeviceRow[];
  onClose: () => void;
}

const NONE = "__none__";
const NEW = "__new__";

function mostCommonTenant(devices: DeviceRow[]): number | null {
  const counts = new Map<number, number>();
  for (const d of devices) {
    if (d.tenant_id != null) counts.set(d.tenant_id, (counts.get(d.tenant_id) ?? 0) + 1);
  }
  let best: number | null = null;
  let bestN = 0;
  for (const [k, n] of counts) {
    if (n > bestN) {
      best = k;
      bestN = n;
    }
  }
  return best;
}

export function TenancyDialog({ devices, onClose }: Props) {
  // Initialize from devices' current tenant; user picks override.
  const [tenantId, setTenantId] = useState<number | null>(() => mostCommonTenant(devices));
  const [reason, setReason] = useState("");
  const [result, setResult] = useState<BulkTenancyResult | null>(null);
  const showToast = useToast((s) => s.show);
  const qc = useQueryClient();

  const tenants = useQuery({
    queryKey: ["tenants"],
    queryFn: () => tenancy.listTenants(),
    staleTime: 5 * 60_000,
  });

  const [creatingNew, setCreatingNew] = useState(false);
  const [newTenantName, setNewTenantName] = useState("");

  const createTenant = useMutation({
    mutationFn: (name: string) => tenancy.createTenant(name),
    onSuccess: (created) => {
      qc.setQueryData<typeof tenants.data>(["tenants"], (prev) => {
        const next = prev ? [...prev, created] : [created];
        next.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        return next;
      });
      setTenantId(created.id);
      setCreatingNew(false);
      setNewTenantName("");
      showToast(`Created tenant "${created.name}".`, "success");
    },
    onError: (err) => {
      showToast(err instanceof Error ? err.message : "tenant creation failed", "error");
    },
  });

  const single = devices.length === 1;
  const selectedSlot = creatingNew ? NEW : tenantId == null ? NONE : String(tenantId);

  const mutation = useMutation({
    mutationFn: async () => {
      if (single) {
        await tenancy.update(devices[0].netbox_id, tenantId, reason);
        return {
          succeeded: [{ device_id: devices[0].netbox_id, tenant_id: tenantId, tenant_name: null }],
          failed: [],
        } as BulkTenancyResult;
      }
      return tenancy.bulk(
        devices.map((d) => d.netbox_id),
        tenantId,
        reason,
      );
    },
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ["inventory"] });
      if (res.failed.length === 0) {
        showToast(`Updated ${res.succeeded.length} device(s).`, "success");
        onClose();
      } else {
        showToast(
          `Updated ${res.succeeded.length}; ${res.failed.length} failed. See details.`,
          "error",
        );
      }
    },
    onError: (err) => {
      showToast(err instanceof Error ? err.message : "tenancy update failed", "error");
    },
  });

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <h2 className="text-base font-semibold mb-1">
          Set tenant for {devices.length} device{devices.length === 1 ? "" : "s"}
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Writes through to Netbox. Choose "— clear tenancy —" to unassign.
        </p>

        <label className="block text-sm mb-3">
          <span className="text-slate-600 dark:text-slate-300">Tenant</span>
          {tenants.isLoading ? (
            <div className="mt-1">
              <Spinner label="Loading tenants from Netbox…" />
            </div>
          ) : tenants.error ? (
            <div className="mt-1 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:bg-red-950/40 dark:border-red-900">
              Couldn't load tenants: {(tenants.error as Error).message}
            </div>
          ) : (
            <>
              <select
                value={selectedSlot}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === NEW) {
                    setCreatingNew(true);
                    return;
                  }
                  setCreatingNew(false);
                  setTenantId(v === NONE ? null : Number(v));
                }}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:bg-slate-800 dark:border-slate-700"
              >
                <option value={NONE}>— clear tenancy —</option>
                <option value={NEW}>+ New tenant…</option>
                {(tenants.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {creatingNew && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    autoFocus
                    value={newTenantName}
                    onChange={(e) => setNewTenantName(e.target.value)}
                    placeholder="New tenant name (e.g. Acme)"
                    className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-700"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newTenantName.trim()) {
                        e.preventDefault();
                        createTenant.mutate(newTenantName.trim());
                      } else if (e.key === "Escape") {
                        setCreatingNew(false);
                        setNewTenantName("");
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => createTenant.mutate(newTenantName.trim())}
                    disabled={!newTenantName.trim() || createTenant.isPending}
                    className="rounded-md bg-brand-500 px-3 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {createTenant.isPending ? "Creating…" : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreatingNew(false);
                      setNewTenantName("");
                    }}
                    className="rounded-md px-2 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </>
          )}
        </label>

        <label className="block text-sm mb-4">
          <span className="text-slate-600 dark:text-slate-300">Reason</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Acme renewed thru 2026-12"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:bg-slate-800 dark:border-slate-700"
          />
        </label>

        {result && result.failed.length > 0 && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 dark:bg-red-950/40 dark:border-red-900">
            <div className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
              {result.failed.length} failed
            </div>
            <ul className="text-xs text-red-800 dark:text-red-300 space-y-0.5 max-h-40 overflow-auto">
              {result.failed.map((f) => (
                <li key={f.device_id}>device {f.device_id}: {f.error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || tenants.isLoading || creatingNew}
            title={creatingNew ? "Finish creating the new tenant first" : undefined}
            className="px-3 py-2 text-sm rounded-md bg-brand-500 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {mutation.isPending ? "Saving…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
